import {
	EntityType,
	Entry,
	EntryProperty,
	EntryPropertyBelongsTo,
	BelongsToPopulatedValue,
	PopulatedEntry,
	PropertyDefinition,
	PropertyDefinitionBoolean,
	PropertyDefinitionHas,
	Repository,
	RepositoryEntry,
	FormSelectOptions,
	RepositoryEntryData,
	PropertyDefinitionBelongsTo,
	ReferenceEntry,
} from '../types';
import pluralize from 'pluralize';
import {ControlledFormData} from '../components/EditEntryForm';
import {nanoid} from 'nanoid';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import calendar from 'dayjs/plugin/calendar';
import {
	ENTRY_PROPERTY_TYPES,
	PROPERTY_DEFINITION_TYPES,
	REPOSITORIES_BY_ENTITY_TYPE,
	SYSTEM_TEMPLATE_PROPERTIES,
} from '../constants';
import { RepositoryEntriesRowDto } from '../services/repositoryEntries';

dayjs.extend(relativeTime);
dayjs.extend(calendar);

/**
 * Converts a desired on-screen size (CSS pixels) into a world-space length for elements that live
 * inside the canvas layer with `transform: scale(scale)`. When `scale` is below 1, the value is
 * divided by `scale` so the size stays roughly constant on screen after scaling.
 */
export function canvasZoomProofWorldPx(screenPx: number, scale: number): number {
	if (!Number.isFinite(screenPx) || !Number.isFinite(scale) || scale <= 0) {
		return screenPx;
	}
	return scale < 1 ? screenPx / scale : screenPx;
}

/** Persisted on repository entry `data`; matches API `REPOSITORY_ENTRY_READABLE_LABEL_KEY`. */
export const REPOSITORY_ENTRY_READABLE_LABEL_KEY = '__readableLabel';

const READABLE_SCALAR_PROPERTY_DEFINITION_TYPES: EntityType[] = [
	EntityType.PropertyDefinitionString,
	EntityType.PropertyDefinitionText,
	EntityType.PropertyDefinitionDate,
	EntityType.PropertyDefinitionDatetime,
	EntityType.PropertyDefinitionNumber,
];

const READABLE_SCALAR_ENTRY_PROPERTY_TYPES: EntityType[] = [
	EntityType.EntryPropertyString,
	EntityType.EntryPropertyText,
	EntityType.EntryPropertyDate,
	EntityType.EntryPropertyDatetime,
	EntityType.EntryPropertyNumber,
];

const getDefaultValue = (def: PropertyDefinition) => {
	switch (def.type) {
	case EntityType.PropertyDefinitionBelongsTo:
	case EntityType.PropertyDefinitionHas:
		return [];
	case EntityType.PropertyDefinitionBoolean:
		return (def as PropertyDefinitionBoolean).defaultChecked || false;
	default:
		return '';
	}
};

const mapRelationValuesToIds = (items: { value: string }[] | undefined): string[] =>
	items?.length ? items.map(({ value }) => value) : [];

export const initFormData = (propertyDefinitions: PropertyDefinition[], propertyValues: any = {}): ControlledFormData => {
	return propertyDefinitions.reduce((acc, def) => {
		if (def.type === EntityType.PropertyDefinitionDate) {
			const raw = propertyValues[def.slug];
			if (raw !== undefined && raw !== null && raw !== '') {
				return {...acc, [def.slug]: dayjs(raw).isValid() ? dayjs(raw).format('YYYY-MM-DD') : ''};
			}
			if (def.required) {
				return {...acc, [def.slug]: new Date().toISOString().split('T')[0]}; // TODO: this doesn't seem to use the time zone of the user.
			}
			return {...acc, [def.slug]: ''};
		}
		if (def.type === EntityType.PropertyDefinitionDatetime) {
			const raw = propertyValues[def.slug];
			if (raw !== undefined && raw !== null && raw !== '') {
				return {...acc, [def.slug]: dayjs(raw).isValid() ? dayjs(raw).format('YYYY-MM-DDTHH:mm') : ''};
			}
			if (def.required) {
				return {...acc, [def.slug]: dayjs().format('YYYY-MM-DDTHH:mm')};
			}
			return {...acc, [def.slug]: ''};
		}
		if (def.type === EntityType.PropertyDefinitionBelongsTo || def.type === EntityType.PropertyDefinitionHas) {
			const rawValues = mapRelationValuesToIds(propertyValues[def.slug]);
			return {...acc, [def.slug]: rawValues};
		}
		return {...acc, [def.slug]: propertyValues[def.slug as keyof Entry] ?? getDefaultValue(def)};
	}, {});
};

export const initEditFormData = (propertyDefinitions: PropertyDefinition[], propertyValues: any = {}): ControlledFormData => {
	return propertyDefinitions.reduce((acc, def) => {
		if (def.type === EntityType.PropertyDefinitionBelongsTo || def.type === EntityType.PropertyDefinitionHas) {
			const rawValues = mapRelationValuesToIds(propertyValues[def.slug]);
			return {...acc, [def.slug]: rawValues};
		}
		if (def.type === EntityType.PropertyDefinitionDatetime) {
			const raw = propertyValues[def.slug];
			return {...acc, [def.slug]: raw ? dayjs(raw).format('YYYY-MM-DDTHH:mm') : ''};
		}
		return {...acc, [def.slug]: propertyValues[def.slug as keyof Entry] ?? getDefaultValue(def)};
	}, {});
};

export const sanitizeEditFormData = (propertyDefinitions: PropertyDefinition[], formData: FormData) => {
	return propertyDefinitions
		.filter((def) => !(PROPERTY_DEFINITION_TYPES.includes(def.type as any) && def.slug === 'slug')) // TODO: this is a hacky way to avoid overriding readonly slug properties while editing property definitions. Real solution is to use a dedicated edit form for property definitions.
		.reduce((acc, def) => {
			const value = formData.get(def.slug);
			if (value === undefined){
				return acc;
			}
			if (def.type === EntityType.PropertyDefinitionBoolean) {
				return {...acc, [def.slug]: value === 'on' ? true : false};
			}
			if (def.type === EntityType.PropertyDefinitionDate) {
				return {...acc, [def.slug]: dayjs(value as string).format('YYYY-MM-DD')};
			}
			if (def.type === EntityType.PropertyDefinitionDatetime) {
				return {...acc, [def.slug]: dayjs(value as string).toISOString()};
			}
			return {...acc, [def.slug]: value};
		}, {} as ControlledFormData);
};

export const sanitizeEditFormDataObject = (propertyDefinitions: PropertyDefinition[], formDataObject: ControlledFormData) => {
	return propertyDefinitions
		.filter((def) => !(PROPERTY_DEFINITION_TYPES.includes(def.type as any) && def.slug === 'slug')) // TODO: this is a hacky way to avoid overriding readonly slug properties while editing property definitions. Real solution is to use a dedicated edit form for property definitions.
		.reduce((acc, def) => {
			const value = formDataObject[def.slug];
			if (value === undefined){
				return acc;
			}
			if (def.type === EntityType.PropertyDefinitionBoolean) {
				const bool =
					typeof value === 'boolean'
						? value
						: value === 'on';
				return {...acc, [def.slug]: bool};
			}
			if (def.type === EntityType.PropertyDefinitionDate) {
				const isValidDate = value && dayjs(value as string).isValid();
				return isValidDate ? {...acc, [def.slug]: dayjs(value as string).format('YYYY-MM-DD')} : {...acc, [def.slug]: null};
			}
			if (def.type === EntityType.PropertyDefinitionDatetime) {
				const isValidDatetime = value && dayjs(value as string).isValid();
				return isValidDatetime ? {...acc, [def.slug]: dayjs(value as string).toISOString()} : {...acc, [def.slug]: null};
			}
			if (def.type === EntityType.PropertyDefinitionNumber) {
				return {...acc, [def.slug]: value ? parseFloat(value as string) : null};
			}
			return {...acc, [def.slug]: value};
		}, {} as ControlledFormData);
};

export const isCustomRepository = (repositoryId: string) => !Object.values(REPOSITORIES_BY_ENTITY_TYPE).find(({ id }) => repositoryId === id);

export const getTemplatePropertiesBySystemRepositoryId = (repositoryId: string) => SYSTEM_TEMPLATE_PROPERTIES.filter((prop) => prop.parentId === repositoryId);

export const generateRandomId = () => nanoid();

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** API records have UUID ids; locally created ones use nanoid. */
export function buildCollectionUrlWithFilters(
	repositoryId: string,
	where: string | null | undefined,
	includeArchived: boolean | null | undefined,
): string {
	const params = new URLSearchParams();
	const whereTrimmed = String(where ?? '').trim();
	if (whereTrimmed) {
		params.set('where', whereTrimmed);
	}
	params.set('includeArchived', includeArchived !== false ? 'true' : 'false');
	return `/collection/${repositoryId}?${params.toString()}`;
}

export const isUuid = (value: string | undefined) => Boolean(value && UUID_PATTERN.test(value));

export const assignRandomId = <T extends object>(obj: T): T & { id: string } => {
	const fakeId = generateRandomId();
	return {
		...obj,
		id: fakeId,
		fakeId
	};
};

export const getEntryProperties = (repositoryEntryId: string, allEntries: Entry[]) => allEntries.filter(({parentId, type}) => ENTRY_PROPERTY_TYPES.includes(type as any) && parentId === repositoryEntryId) as EntryProperty[]; // TODO: rename this to 'getEntryPropertyValues'?

export const getEntryRemoteProperties = (repositoryEntryId: string, allEntries: Entry[]) => allEntries.filter((entry) => [EntityType.EntryPropertyBelongsTo].includes(entry.type as any) && (entry as EntryPropertyBelongsTo).value === repositoryEntryId) as EntryPropertyBelongsTo[]; // populating has property values. TODO: rename this.

export const populateSelectOptionsRepositoryEntries = (propertyDefinitions: PropertyDefinition[], repositoryEntries: RepositoryEntry[], entryReadableValues: Map<string, string>) => {
	return propertyDefinitions
		.filter((prop) => [EntityType.PropertyDefinitionBelongsTo, EntityType.PropertyDefinitionHas].includes(prop.type))
		.reduce((acc, {slug, targetRepositoryId}: any) => {
			const repositoryEntryOptions = repositoryEntries
				.filter((entry) => entry.parentId === targetRepositoryId && !entry.isArchived)
				.map((repositoryEntry) => ({ label: entryReadableValues.get(repositoryEntry.id), value: repositoryEntry.id }));
			const allSelectOptions = [
				...repositoryEntryOptions,
			];
			return {...acc, [slug]: allSelectOptions};
		}, {});
};

export const populateSelectOptionsRepositories = (propertyDefinitions: PropertyDefinition[], allEntries: Entry[]): FormSelectOptions => {
	return propertyDefinitions
		.filter((prop) => prop.type === EntityType.PropertyDefinitionBelongsTo)
		.reduce((acc, {slug}: any) => {
			const repositoryOptions = allEntries
				.filter(({ type }) => type === EntityType.Repository)
				.map((repository) => ({ label: (repository as Repository).title , value: repository.id }));
			const allSelectOptions = [
				...repositoryOptions,
			];
			return {...acc, [slug]: allSelectOptions};
		}, {});
};

export const getEditFormTemplatePropertiesForEntityType = (entityType: EntityType) => {
	return TEMPLATES_BY_ENTITY_TYPE[entityType].map((propertyDefinition: PropertyDefinition) => {
		if (propertyDefinition.slug === 'slug') {
			return {...propertyDefinition, disabled: true};
		} else {
			return propertyDefinition;
		}
	});
};

export const relationPropertyValueAsObjectArray = (value: unknown): BelongsToPopulatedValue[] => {
	if (value == null) {
		return [];
	}
	if (!Array.isArray(value)) {
		if (process.env.NODE_ENV === 'development') {
			// eslint-disable-next-line no-console
			console.warn(
				'[relationPropertyValueAsObjectArray] Expected relation field to be an object array from API/populate; got',
				typeof value,
			);
		}
		return [];
	}
	return value as BelongsToPopulatedValue[];
};

function isRelationValueArray(val: unknown[]): boolean {
	const first = val[0];
	if (first == null || typeof first !== 'object') {
		return false;
	}
	const o = first as Record<string, unknown>;
	const targetId =
		typeof o.value === 'string'
			? o.value
			: typeof o.targetEntryId === 'string'
				? o.targetEntryId
				: null;
	return typeof o.id === 'string' && Boolean(targetId);
}

function normalizeRelationValueArray(
	val: unknown[],
): BelongsToPopulatedValue[] {
	return val.map((item) => {
		const o = item as Record<string, unknown>;
		const targetId =
			typeof o.value === 'string'
				? o.value
				: typeof o.targetEntryId === 'string'
					? o.targetEntryId
					: '';
		return {
			id: String(o.id ?? ''),
			label: typeof o.label === 'string' ? o.label : '',
			value: targetId,
		};
	});
}

/**
 * Repository entries API rows use `targetEntryId` (and optional metadata) on relation cells;
 * the rest of the UI expects `value` (target entry id) like local populate.
 */
export function repositoryEntriesApiRowToPopulatedEntry(row: {
	id: string;
	label: string;
	rank: number | string;
	isArchived: boolean;
	data: Record<string, unknown>;
}): PopulatedEntry {
	const data: Record<string, unknown> = { ...row.data };
	for (const key of Object.keys(data)) {
		const val = data[key];
		if (!Array.isArray(val) || val.length === 0) {
			continue;
		}
		if (isRelationValueArray(val)) {
			data[key] = normalizeRelationValueArray(val);
		}
	}
	return {
		id: row.id,
		label: row.label,
		rank: row.rank,
		isArchived: row.isArchived,
		data,
	};
}

/** Schema slugs populated from `repository_entries.created_at` / `updated_at` (not user-editable). */
export const ENTRY_META_PROPERTY_SLUGS: readonly string[] = ['created-at', 'updated-at'];

export function isEntryTimestampPropertyDefinition(def: PropertyDefinition): boolean {
	return (
		(ENTRY_META_PROPERTY_SLUGS as readonly string[]).includes(def.slug) &&
		(def.type === EntityType.PropertyDefinitionDate ||
			def.type === EntityType.PropertyDefinitionDatetime)
	);
}

const URL_REGEX = /https?:\/\/[^\s<>"']+/gi;

/** Extracts http(s) URLs from free text, trimming trailing punctuation picked up by the sloppy match. */
export function extractUrlsFromText(text: string): string[] {
	const matches = text.match(URL_REGEX);
	return matches ? matches.map((url) => url.replace(/[.,;:!?)]+$/, '')) : [];
}

const TEXT_PROPERTY_DEFINITION_TYPES: EntityType[] = [
	EntityType.PropertyDefinitionString,
	EntityType.PropertyDefinitionText,
];

/** Extracts URLs from a single property value when the property is a string/text field. */
export function extractPropertyValueUrls(def: PropertyDefinition, value: unknown): string[] {
	return TEXT_PROPERTY_DEFINITION_TYPES.includes(def.type as EntityType) && typeof value === 'string'
		? extractUrlsFromText(value)
		: [];
}

/** Collects unique URLs found across an entry's string/text property values (e.g. for a reference's target entry). */
export function extractEntryTextFieldUrls(
	propertyDefinitions: PropertyDefinition[],
	data: Record<string, unknown> | null | undefined,
): string[] {
	if (!data) {
		return [];
	}
	const urls = propertyDefinitions.flatMap((def) => extractPropertyValueUrls(def, data[def.slug]));
	return Array.from(new Set(urls));
}

export function getUrlFaviconSrc(url: string): string {
	return `https://www.google.com/s2/favicons?sz=32&domain=${new URL(url).hostname}`;
}

export const entryPropertyGetReadableValue = (propertyDefinition: PropertyDefinition, value: any) => {
	switch (propertyDefinition.type) {
	case EntityType.PropertyDefinitionDate:
		return value ? dayjs(value).format(dayjs(value).isSame(dayjs(), 'year') ? 'ddd D. MMM' : 'ddd D. MMM YYYY') : '';
	case EntityType.PropertyDefinitionDatetime:
		return value ? dayjs(value).format(dayjs(value).isSame(dayjs(), 'year') ? 'ddd D. MMM HH:mm' : 'ddd D. MMM YYYY HH:mm') : '';
	case EntityType.PropertyDefinitionBoolean:
		return value === true ? 'Yes' : 'No';
	case EntityType.PropertyDefinitionBelongsTo:
	case EntityType.PropertyDefinitionHas: {
		const items = relationPropertyValueAsObjectArray(value);
		if (!items.length) {
			return '';
		}
		return items.slice(0, 2).map(({ label, value: id }) => (label ?? id ?? '')).join(', ')
			+ (items.length > 2 ? ` and ${items.length - 2} more` : '');
	}
	default:
		return value;
	}
};

export const getRelativeTimeDay = (value: string) => dayjs(value).calendar(undefined, {
	sameDay: '[Today]',
	nextDay: '[Tomorrow]',
	lastDay: '[Yesterday]',
	sameWeek: '[This week]',
	nextWeek: () => dayjs(value).add(1, 'day').fromNow(),
	lastWeek: () => dayjs(value).add(1, 'day').fromNow(),
	sameElse: () => dayjs(value).add(1, 'day').fromNow()
});

export const getRelativeTimeDatetime = (value: string) => dayjs(value).calendar(undefined, {
	sameDay: () => dayjs(value).fromNow(),
	nextDay: () => dayjs(value).fromNow(),
	lastDay: () => dayjs(value).fromNow(),
	sameWeek: () => dayjs(value).fromNow(),
	nextWeek: () => dayjs(value).fromNow(),
	lastWeek: () => dayjs(value).fromNow(),
	sameElse: () => dayjs(value).fromNow()
});

export const entryPropertyGetSecondaryValue = (propertyDefinition: PropertyDefinition, value: any): string | null => {
	switch (propertyDefinition.type) {
	case EntityType.PropertyDefinitionDate:{
		const day = getRelativeTimeDay(value);
		return value ? day : null;
	}
	case EntityType.PropertyDefinitionDatetime:{
		const relativeTime = getRelativeTimeDatetime(value);
		return value ? relativeTime : null;
	}
	default:
		return null;
	}
};

export const getRepositorySingleUnitLabel = (repository: Pick<Repository, 'singular'>) =>
	repository.singular ?? '';

export const getRepositorySingularFromTitle = (title: string): string => {
	const trimmedTitle = title.trim();

	if (!trimmedTitle) {
		return '';
	}

	if (pluralize.isPlural(trimmedTitle)) {
		return pluralize.singular(trimmedTitle);
	}

	return `${trimmedTitle} item`;
};

/**
 * In the future, when populating entries takes place in the back-end, this method will deprecate as every entry will have 'readableValue' on them.
 */
export const entryGetReadableValue = (entry: Entry, entryReadableValues: Map<string, string>): string => {
	switch (entry.type) {
	case EntityType.Reference: {
		const targetEntryLabel = entryReadableValues.get((entry as ReferenceEntry).repositoryEntryId);
		if (!targetEntryLabel) {
			console.error(`target entry inaccessible: ${entry.repositoryEntryId}`);
			return entry.repositoryEntryId;
		}
		return targetEntryLabel;
	}
	case EntityType.EntryPropertyBelongsTo: {
		const targetEntryLabel = entryReadableValues.get(entry.value);
		if (!targetEntryLabel) {
			console.error(`target entry inaccessible: ${entry.value}`);
			return entry.value;
		}
		return targetEntryLabel;
	}
	case EntityType.RepositoryEntry: {
		const fromMap = entryReadableValues.get(entry.id);
		if (fromMap) {
			return fromMap;
		}
		const populatedLabel = (entry as RepositoryEntry & Pick<PopulatedEntry, 'label'>).label;
		if (populatedLabel) {
			return populatedLabel;
		}
		console.error(`entry inaccessible: ${entry.id}`);
		return entry.id;
	}
	case EntityType.Note:
	case EntityType.Repository:
	case EntityType.Query:
	case EntityType.Chart: {
		return entry.title;
	}
	case EntityType.Grid:
		return '';

	case EntityType.EntryPropertyBoolean:
		return entry.value ? 'Yes' : 'No';
	default: {
		if (ENTRY_PROPERTY_TYPES.includes(entry.type as any)) {
			return (entry as EntryProperty).value as string;
		}
		if (PROPERTY_DEFINITION_TYPES.includes(entry.type as any)) {
			return (entry as PropertyDefinition).label as string;
		}
		console.warn(`no readable label for entry ${entry.id}`);
		return '';
	}
	}
};

const filterPropertiesByDef = (prop: EntryProperty, def: PropertyDefinition) => prop.slug === def.slug;

export const filterPropertiesByHasDef = (prop: EntryPropertyBelongsTo, def: PropertyDefinitionHas) => prop.repositoryId === (def as PropertyDefinitionHas).targetRepositoryId && prop.slug === (def as PropertyDefinitionHas).targetPropertySlug;

const mapHasPropsToRelationCells = (
	props: EntryPropertyBelongsTo[],
	def: PropertyDefinitionHas,
	idProps: Map<string, string>,
): BelongsToPopulatedValue[] =>
	props
		.filter((prop) => filterPropertiesByHasDef(prop, def))
		.map((prop) => ({
			id: String(prop.id),
			value: String(prop.parentId),
			label: idProps.get(String(prop.parentId)) ?? '',
		}));

const mapBelongsToPropsToRelationCells = (
	props: EntryProperty[],
	def: PropertyDefinitionBelongsTo,
	idProps: Map<string, string>,
): BelongsToPopulatedValue[] =>
	props
		.filter((prop) => filterPropertiesByDef(prop, def))
		.map((prop) => ({
			id: String(prop.id),
			value: String(prop.value),
			label: idProps.get(String(prop.value)) ?? '',
		}));

export const populateRepositoryEntry = (repositoryEntry: RepositoryEntry, entryProperties: EntryProperty[], entryRemoteProperties: EntryPropertyBelongsTo[], propertyDefinitions: PropertyDefinition[], idProps: Map<string, string>): PopulatedEntry => {
	const currentEntryProperties = entryProperties.filter(({ parentId }) => parentId === repositoryEntry.id);
	const currentEntryRemoteProperties = entryRemoteProperties.filter(({ value }) => value === repositoryEntry.id);

	const dataObject: RepositoryEntryData = propertyDefinitions.reduce((acc, def) => {
		switch (def.type) {
		case EntityType.PropertyDefinitionHas: {
			return {
				...acc,
				[def.slug]: mapHasPropsToRelationCells(
					currentEntryRemoteProperties,
					def as PropertyDefinitionHas,
					idProps,
				),
			};
		}
		case EntityType.PropertyDefinitionBelongsTo: {
			return {
				...acc,
				[def.slug]: mapBelongsToPropsToRelationCells(
					currentEntryProperties,
					def as PropertyDefinitionBelongsTo,
					idProps,
				),
			};
		}
		default: {
			const [fieldProperty] = currentEntryProperties.filter((prop) => filterPropertiesByDef(prop, def));
			return {...acc, [def.slug]: fieldProperty ? fieldProperty.value : null};
		}
		}
	}, {});
	return {id: repositoryEntry.id, label: idProps.get(repositoryEntry.id) as string, data: dataObject, isArchived: repositoryEntry.isArchived ?? false, rank: repositoryEntry.rank ?? 'a0'};
};

/**
 * Same rules as API `repository-entries-populate.util.ts` `buildEntryReadableValuesMap`:
 * lowest-rank “first readable” per repository may be a scalar or a BelongsTo; labels propagate in a fixed-point loop.
 * @param seedLabels — already-known labels (e.g. other repositories in a prior round).
 */
export function buildEntryReadableValuesMap(
	propertyDefinitions: PropertyDefinition[],
	entryProperties: EntryProperty[],
	seedLabels: Map<string, string> = new Map(),
): Map<string, string> {
	const firstReadableCandidates = propertyDefinitions.filter(
		(def) =>
			READABLE_SCALAR_PROPERTY_DEFINITION_TYPES.includes(def.type as EntityType) ||
			def.type === EntityType.PropertyDefinitionBelongsTo,
	);

	const firstReadablePropertyDefsPerRepository = firstReadableCandidates
		.sort(sortEntryByRank)
		.reduce((acc, def) => {
			const pid = def.parentId;
			return acc.has(pid) ? acc : acc.set(pid, def);
		}, new Map<string, PropertyDefinition>());

	const propsByParentId = new Map<string, EntryProperty[]>();
	for (const p of entryProperties) {
		const pid = String(p.parentId);
		const list = propsByParentId.get(pid) ?? [];
		list.push(p);
		propsByParentId.set(pid, list);
	}

	const result = new Map(seedLabels);

	let changed = true;
	while (changed) {
		changed = false;
		for (const [parentId, plist] of Array.from(propsByParentId.entries())) {
			const repoId = String(plist[0].repositoryId);
			const firstDef = firstReadablePropertyDefsPerRepository.get(repoId);
			if (!firstDef) {
				continue;
			}

			const prop = plist.find(
				(p: EntryProperty) => String(p.propertyDefinitionId) === String(firstDef.id),
			);
			if (!prop) {
				continue;
			}

			let nextLabel: string;
			if (READABLE_SCALAR_ENTRY_PROPERTY_TYPES.includes(prop.type as EntityType)) {
				if (!READABLE_SCALAR_PROPERTY_DEFINITION_TYPES.includes(firstDef.type as EntityType)) {
					continue;
				}
				nextLabel = entryPropertyGetReadableValue(firstDef, prop.value);
			} else if (
				prop.type === EntityType.EntryPropertyBelongsTo &&
				firstDef.type === EntityType.PropertyDefinitionBelongsTo
			) {
				const refId = String(prop.value ?? '');
				nextLabel = refId ? (result.get(refId) ?? '') : '';
			} else {
				continue;
			}

			if (result.get(parentId) !== nextLabel) {
				result.set(parentId, nextLabel);
				changed = true;
			}
		}
	}

	return result;
}

/**
 * Mirrors API `buildReadableLabelMapForEntryIds` and `resolveReadableLabelsWithFallback`:
 * multi-repository rounds, then non-empty persisted `REPOSITORY_ENTRY_READABLE_LABEL_KEY` wins.
 */
export function buildReadableLabelMapForLocalEntries(repositoryEntries: RepositoryEntry[], propertyDefinitions: PropertyDefinition[], entryProperties: EntryProperty[]): Map<string, string> {
	const repoIds = Array.from(
		new Set(repositoryEntries.map((e) => e.repositoryId)),
	);
	if (!repoIds.length) {
		return new Map();
	}

	const defsByRepo = new Map<string, PropertyDefinition[]>();
	const propsByRepo = new Map<string, EntryProperty[]>();
	for (const repoId of repoIds) {
		defsByRepo.set(
			repoId,
			propertyDefinitions.filter(
				(def) => def.parentId === repoId,
			)
		);
		propsByRepo.set(
			repoId,
			entryProperties.filter(
				(prop) => prop.repositoryId === repoId,
			),
		);
	}

	const merged = new Map<string, string>();
	const maxRounds = 25;
	for (let round = 0; round < maxRounds; round++) {
		let changed = false;
		for (const repoId of repoIds) {
			const partial = buildEntryReadableValuesMap(
				defsByRepo.get(repoId) ?? [],
				propsByRepo.get(repoId) ?? [],
				merged,
			);
			for (const [id, label] of Array.from(partial.entries())) {
				if (merged.get(id) !== label) {
					merged.set(id, label);
					changed = true;
				}
			}
		}
		if (!changed) {
			break;
		}
	}

	for (const e of repositoryEntries) {
		const raw = (e as RepositoryEntry & { data?: Record<string, unknown> }).data?.[
			REPOSITORY_ENTRY_READABLE_LABEL_KEY
		];
		if (typeof raw === 'string' && raw.trim().length > 0) {
			merged.set(e.id, raw.trim());
		}
	}

	return merged;
}

/** Client-side table rows for a repository (offline / unauthenticated). */
export const populateRepositoryEntriesForTable = (repository: Repository, allRepositoryEntries: RepositoryEntry[], allPropertyDefinitions: PropertyDefinition[], allEntryProperties: EntryProperty[]): RepositoryEntriesRowDto[] => {
	const idProps = buildReadableLabelMapForLocalEntries(
		allRepositoryEntries,
		allPropertyDefinitions,
		allEntryProperties,
	);
	const currentRepositoryEntries = allRepositoryEntries.filter((entry) => entry.repositoryId === repository.id);
	const currentRepositoryEntryProperties = allEntryProperties.filter((entry) => entry.repositoryId === repository.id) as EntryProperty[];
	const propertyDefinitions = allPropertyDefinitions.filter((def) => def.parentId === repository.id) as PropertyDefinition[];
	const currentRepositoryRemoteEntryProperties = allEntryProperties.filter((entry) => (entry as any).type === EntityType.EntryPropertyBelongsTo && (entry as EntryPropertyBelongsTo).targetRepositoryId === repository.id) as EntryPropertyBelongsTo[];
	return currentRepositoryEntries.map((repositoryEntry) => populateRepositoryEntry(repositoryEntry, currentRepositoryEntryProperties, currentRepositoryRemoteEntryProperties, propertyDefinitions, idProps));
};

export const populateEntryData = (entry: Entry): Entry & {data: any} => {
	const propertyDefinitions = TEMPLATES_BY_ENTITY_TYPE[entry.type];
	const dataObject = propertyDefinitions.reduce((acc, def) => {
		const value = entry[def.slug as keyof Entry];
		switch (def.type) {
		case EntityType.PropertyDefinitionBelongsTo: {
			return {...acc, [def.slug]: value ? [{ value }] : []};
		}
		default: {
			return {...acc, [def.slug]: value };
		}
		}
	}, {});
	return {...entry, data: dataObject};
};

function rankSortKeyAny(rank: unknown): string {
	if (rank === undefined || rank === null) {
		return '\uffff';
	}
	if (typeof rank === 'number' && Number.isFinite(rank)) {
		return String(Math.floor(rank)).padStart(24, '0');
	}
	return String(rank);
}

/** Ascending order: fractional-index strings, legacy integers (padded), then missing rank last. */
export function compareRepositoryEntryRank(
	a: { rank?: number | string },
	b: { rank?: number | string },
): number {
	const ka = rankSortKeyAny(a.rank);
	const kb = rankSortKeyAny(b.rank);
	return ka < kb ? -1 : ka > kb ? 1 : 0;
}

export const sortEntryByRank = (a: any, b: any) => compareRepositoryEntryRank(a, b);

export const TEMPLATES_BY_ENTITY_TYPE: Record<EntityType, PropertyDefinition[]> = Object.entries(REPOSITORIES_BY_ENTITY_TYPE).reduce((acc, [entityType, repository]) => {
	return {...acc, [entityType]: getTemplatePropertiesBySystemRepositoryId(repository.id)};
}, {} as Record<EntityType, PropertyDefinition[]>);

export function recursiveChildRecordIdsFind(id: string, allEntries: Entry[]): string[] {
	const allIds = [];
	for (const entry of allEntries) {
		// TODO: handle circular dependency?
		if (entry.parentId === id) {
			allIds.push(entry.id, ...recursiveChildRecordIdsFind(entry.id, allEntries));
		}
	}
	return allIds;
}

export function findPropertiesWithUnknownDefinitions(allEntries: Entry[]): string[] {
	const allTemplateProperties = allEntries.filter((entry: any) => PROPERTY_DEFINITION_TYPES.includes(entry.type)) as PropertyDefinition[];
	const allEntryProperties = allEntries.filter((entry: any) => ENTRY_PROPERTY_TYPES.includes(entry.type)) as EntryProperty[];
	return allEntryProperties
		.filter((entryProperty: EntryProperty) =>
			!allTemplateProperties.find((propertyDefinition: PropertyDefinition) =>
				entryProperty.repositoryId === propertyDefinition.parentId && entryProperty.slug === propertyDefinition.slug))
		.map(({id}) => id);
}

export function findEntryPropertiesWithUnknownTargetEntry(allEntries: Entry[]): string[] {
	const allEntryProperties = allEntries.filter((entry: any) => ENTRY_PROPERTY_TYPES.includes(entry.type)) as EntryProperty[];
	const allBelongsToProperties = allEntryProperties.filter((entry: any) => [EntityType.EntryPropertyBelongsTo].includes(entry.type)) as EntryPropertyBelongsTo[];
	const allRepositoryEntriesMap = allEntries.reduce((acc, entry: any) => [EntityType.RepositoryEntry].includes(entry.type) ? acc.set(entry.id, entry) : acc, new Map<string, RepositoryEntry>());
	return allBelongsToProperties
		.filter((entryPropertyBelongsToRecord: EntryPropertyBelongsTo) => {
			const targetEntry = allRepositoryEntriesMap.get(entryPropertyBelongsToRecord.value);
			return !targetEntry || targetEntry.repositoryId !== entryPropertyBelongsToRecord.targetRepositoryId;
		})
		.map(({id}) => id);
}

/** Canvas Reference rows whose `repositoryEntryId` is no longer present (e.g. after local entry delete). */
export function findReferenceIdsPointingToRepositoryEntryIds(
	repositoryEntryIds: string[],
	allEntries: Entry[],
): string[] {
	const targetIds = new Set(repositoryEntryIds);
	return allEntries
		.filter(
			(entry): entry is ReferenceEntry =>
				entry.type === EntityType.Reference &&
				targetIds.has((entry as ReferenceEntry).repositoryEntryId),
		)
		.map(({ id }) => id);
}
